import isEqual from 'lodash/isEqual';
import isFunction from 'lodash/isFunction';
import isNil from 'lodash/isNil';
import includes from 'lodash/includes';
import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useStore} from 'react-redux';
import {renderRoutes} from 'react-router-config';
import Arg from '@spothero/arg';
import RouterLoader from 'router/RouterLoader';
import {Route, useLocation} from 'react-router-dom';
import routes, {matchPathToRoute} from 'router/routes';
import {dataLoadFulfilled} from './router/router-actions';
import {updateData as updateSearchRequest} from './search-request/search-request-actions';
import {Page} from './page/utils';
import NotFound from './router/NotFound';

// Get state for next route/page via `getInitialState` and publish to reducers
export async function reduceStateForRoute(
    prevLocation,
    location,
    dispatch,
    getState,
    setIsLoading
) {
    const {search: previousSearch} = prevLocation;
    const {pathname, search, state: historyState = {}} = location;
    const route = matchPathToRoute(pathname);
    const query = Arg.parse(search);
    const previousQuery = Arg.parse(previousSearch);
    const querySpotId = query['spot-id'];
    const prevQuerySpotId = previousQuery['spot-id'];

    if (isNil(route)) {
        throw new Error(`Unable to match path to route: ${pathname}`);
    }

    if (route.isSearch && (querySpotId || (prevQuerySpotId && !querySpotId))) {
        // let the search component handle showing and hiding spot details
        // (aka when ?spot-id is added or removed from the URL which is a history push)
        return;
    }

    const {component, params} = route;
    const isReplacedRoute = historyState.method === 'replace';

    if (!isReplacedRoute && isFunction(component.getInitialState)) {
        setIsLoading(true);

        const newState = await component.getInitialState({
            state: getState(),
            params,
            query,
            route: {location},
        });

        dispatch(dataLoadFulfilled(newState));
    }

    // In the case where:
    // - we are replacing the URL
    // - and do not want to fetch new data
    // - but do need to change the page type
    // for checkout, this occurs when adding new URL query parameters
    if (isReplacedRoute && pathname.startsWith('/checkout/')) {
        dispatch(updateSearchRequest({pageType: Page.CHECKOUT}));
    }
}

const getPath = ({pathname}) => {
    const {isSearch} = matchPathToRoute(pathname) || {};

    return !includes(pathname, 'airport') && isSearch ? '/search' : pathname;
};

function Navigator({extraProps}) {
    const dispatch = useDispatch();
    const store = useStore();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [isNotFound, setIsNotFound] = useState(false);
    const [path, setPath] = useState(getPath(location));
    const prevLocationRef = useRef(null);

    useEffect(() => {
        const prevLocation = prevLocationRef.current;

        prevLocationRef.current = location;

        if (prevLocation === null) return;

        if (
            location.pathname !== prevLocation.pathname ||
            location.search !== prevLocation.search ||
            !isEqual(location.state, prevLocation.state)
        ) {
            reduceStateForRoute(
                prevLocation,
                location,
                dispatch,
                store.getState,
                setIsLoading
            )
                .then(() => {
                    setPath(getPath(location));
                    setIsNotFound(false);
                    setIsLoading(false);
                })
                .catch(() => {
                    setIsNotFound(true);
                    setIsLoading(false);
                });
        }
    }, [location, dispatch, store]);

    return (
        <Route
            location={{pathname: path}}
            render={() =>
                isNotFound ? (
                    <NotFound />
                ) : isLoading ? (
                    <RouterLoader />
                ) : (
                    renderRoutes(routes, extraProps)
                )
            }
        />
    );
}

Navigator.propTypes = {
    extraProps: PropTypes.object.isRequired,
};

export default Navigator;
